import LikeButton from '@components/buttons/LikeButton';
import PrimaryImage from '@components/primary-image/PrimaryImage';
import useDocument from '@hooks/useDocument';
import shortAddress from '@ui/utils/shortAddress';
import slugOrID from '@ui/utils/slugOrID';
import staticMap from '@ui/utils/staticMap';
import Link from 'next/link';
import { parseAddress } from 'parse-address';
import React, { CSSProperties, useMemo } from 'react';

import useStyles from './HomeCard.style';

interface HomeCardProps {
  home?: guesthouse.HomeLite;
  imageHeight?: string | number;
  style?: CSSProperties;
  className?: string;
  onClick?: (home: guesthouse.HomeLite) => void;
  saved?: boolean;
  onSavedClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  renderCta?: (home: guesthouse.Home, className: string) => React.ReactElement;
  usePhotosLink?: boolean;
}

const HomeCard: React.FC<HomeCardProps> = (props) => {
  const {
    className,
    home,
    onClick,
    style,
    saved,
    onSavedClick,
    imageHeight,
    renderCta,
    usePhotosLink
  } = props;
  const { classes, cx } = useStyles();
  const styleObj: CSSProperties = { ...style };

  const { doc: homeDoc } = useDocument(`homes/${home.docID}`);

  const fullHome = useMemo(() => {
    if (homeDoc?.exists()) {
      return homeDoc.data() as guesthouse.Home;
    }
  }, [homeDoc]);

  const parsedAddress = useMemo(() => {
    if (home.address) {
      return parseAddress(home.address.replace(', USA', ''));
    } else if (fullHome?.address) {
      return parseAddress(fullHome.address.replace(', USA', ''));
    } 

    return {
      city: '',
      state: '',
    };
  }, [home, fullHome]);

  const short = useMemo(() => {
    if (home.address) {
      return shortAddress(home.address);
    } else if (fullHome?.address) {
      return shortAddress(fullHome.address);
    }
  }, [home]);


  return (
    <div
      style={{
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      <Link
        href={usePhotosLink ? `/shop/homes/${slugOrID(home)}/photos` : `/shop/homes/${slugOrID(home)}`}
        style={{
          textDecoration: 'none',
        }}
      >

        <div
          className={cx(classes.container, className)}
          style={styleObj}
          onClick={() => typeof onClick === 'function' && onClick(home)}
        >
          <div
            className={classes.imageWrapper}
            tabIndex={0}
            style={{
              height: imageHeight,
            }}
          >
            <PrimaryImage
              item={fullHome}
              fallback={staticMap(home.address)}
              className={classes.image}
            />
          </div>
          <address
            className={classes.address}
            style={{ fontStyle: 'normal' }}
          >
            {short}
            {(home || fullHome).unit && `, Unit ${(home || fullHome).unit}`}
          </address>
          {parsedAddress?.city && parseAddress?.state &&
            <address className={classes.cityState}>
              {`${parsedAddress.city}, ${parsedAddress.state}`}
            </address>
          }
          {renderCta &&
            <div className={classes.listedForContainer}>
              {typeof renderCta === 'function' && (
                <>
                  {renderCta(fullHome, classes.cta)}
                </>
              )}
            </div>
          }
        </div>

      </Link>
      {onSavedClick && (
        <div
          className={classes.like}
        >
          <LikeButton
            large
            enabled={saved}
            variant="outlined"
            onClick={onSavedClick}
          />
        </div>
      )}
    </div>
  );
};

HomeCard.defaultProps = {
  className: null,
  home: {} as guesthouse.HomeLite,
  onClick: null,
  onSavedClick: null,
  style: {},
  saved: false,
};

export default HomeCard;
